import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export interface LeverPosting {
  id: string;
  title: string;
  state: string | null;
  team: string | null;
  location: string | null;
}

export interface PostingLookup {
  postings: LeverPosting[];
  /** False when Lever could not be reached — the UI falls back to manual entry. */
  available: boolean;
  error?: string;
}

/**
 * Find a Lever posting so nobody has to type a UUID.
 *
 * Two lookups, because Lever's API only supports these two cheaply:
 *  - `query`          → matches posting title, team or location.
 *  - `candidateEmail` → exact email match, returns that candidate's postings.
 *
 * Searching by candidate NAME is not possible: Lever's opportunities endpoint
 * filters on email, tag, stage and posting — not on name — so a name search would
 * mean paging the whole account.
 */
export async function lookupPostings(
  args: { query?: string; candidateEmail?: string }
): Promise<PostingLookup> {
  const params = new URLSearchParams();
  if (args.candidateEmail) params.set('candidateEmail', args.candidateEmail);
  else params.set('q', args.query || '');

  try {
    const res = await authenticatedFetch(`${PROXY_URL}/api/lever/postings?${params}`);
    if (!res.ok) return { postings: [], available: false, error: `HTTP ${res.status}` };
    return (await res.json()) as PostingLookup;
  } catch (err) {
    return { postings: [], available: false, error: err instanceof Error ? err.message : 'failed' };
  }
}

/** The Lever title of each posting id — empty when Lever is unreachable, so callers fall back. */
export async function fetchPostingsByIds(ids: string[]): Promise<LeverPosting[]> {
  if (ids.length === 0) return [];
  const params = new URLSearchParams({ ids: ids.join(',') });
  try {
    const res = await authenticatedFetch(`${PROXY_URL}/api/lever/postings?${params}`);
    if (!res.ok) return [];
    return ((await res.json()) as PostingLookup).postings ?? [];
  } catch {
    return [];
  }
}

export interface LeverStage {
  id: string;
  name: string;
}

/** Lever pipeline stages, in pipeline order — the source for the stage dropdown. */
export async function fetchLeverStages(): Promise<{ stages: LeverStage[]; available: boolean }> {
  try {
    const res = await authenticatedFetch(`${PROXY_URL}/api/lever/stages`);
    if (!res.ok) return { stages: [], available: false };
    return (await res.json()) as { stages: LeverStage[]; available: boolean };
  } catch {
    return { stages: [], available: false };
  }
}

export interface LeverFeedbackTemplate {
  id: string;
  name: string;
  /** Lever's own template group, e.g. "🎒 Internships". */
  group: string | null;
}

/** Every feedback form in Lever — the source for the link's feedback-form picker. */
export async function fetchFeedbackTemplates(): Promise<{
  templates: LeverFeedbackTemplate[];
  available: boolean;
}> {
  try {
    const res = await authenticatedFetch(`${PROXY_URL}/api/lever/feedback-templates`);
    if (!res.ok) return { templates: [], available: false };
    return (await res.json()) as { templates: LeverFeedbackTemplate[]; available: boolean };
  } catch {
    return { templates: [], available: false };
  }
}

export interface FeedbackTemplateSuggestion {
  templateId: string;
  /** How many past interviews used it, out of how many sampled — shown as evidence. */
  used: number;
  total: number;
}

/**
 * The form previous interviews for this posting+stage used.
 *
 * A prefill only. Lever exposes nothing that names the right template — no field
 * on the posting, and every template has `stage: null` — so history is the only
 * signal, and it is split often enough (Final & Bar Raiser alternates between two
 * forms on the same posting) that the TA manager must confirm it.
 */
export async function fetchFeedbackTemplateSuggestion(
  postingId: string,
  stageId: string
): Promise<FeedbackTemplateSuggestion | null> {
  if (!postingId || !stageId) return null;
  const params = new URLSearchParams({ postingId, stageId });
  try {
    const res = await authenticatedFetch(
      `${PROXY_URL}/api/lever/feedback-templates/suggestion?${params}`
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { suggestion: FeedbackTemplateSuggestion | null };
    return body.suggestion ?? null;
  } catch {
    return null;
  }
}
